// https://www.gatsbyjs.org/docs/browser-apis/
import * as Sentry from '@sentry/react';
import { Integrations as TracingIntegrations } from '@sentry/tracing';
import './src/assets/styles/reset.css';
import './src/assets/styles/global.css';
import './src/assets/styles/variables.css';
import './src/assets/styles/util.css';
import './src/assets/styles/article-card.css';
import './src/assets/styles/posts-list.css';
import './src/assets/styles/theme.css';
import ThemeProvider from './src/components/context/ThemeProvider';
import AnalyticsProvider from './src/components/context/AnalyticsProvider';
import ImageLazyloadProvider from './src/components/context/ImageLazyloadProvider';
import PageAnimation from './src/components/common/PageAnimation';
import RootElementMDXProvider from './src/components/common/RootElementMDXProvider';
import Header from './src/components/Header';

/**
 * This inits Sentry only in production. The `GATSBY_SENTRY_RELEASE` value
 * is set and passed from the gatsby-config.js file, because environment variables can only be set there.
 * Important: the release version number can only be obtained from running npm run
 * build and NOT gatsby build.
 */
export const onClientEntry = () => {
  if (process.env.NODE_ENV === 'production') {
    Sentry.init({
      dsn: 'https://cad8a2f7ecdb4f3c93863f8aef38b23b@o416831.ingest.sentry.io/5313532',
      environment: process.env.NODE_ENV,
      release: process.env.GATSBY_SENTRY_RELEASE || 'release-not-specified',
      integrations: [new TracingIntegrations.BrowserTracing()],

      // 50% sample rate will be sent to sentry. Adjust as needed accounting for monthly traffic
      // https://docs.sentry.io/platforms/javascript/guides/react/performance/#configure-the-sample-rate
      tracesSampleRate: 0.50,
    });
    window.Sentry = Sentry;
  }
};

/**
 * Wraps the page element with a page animation component
 * The Header component needs to be added at this level, otherwise
 * each route change re-runs the animation
 */
 export const wrapPageElement = ({ element }) => (
  <ImageLazyloadProvider>
    <Header />
    <PageAnimation>
      {element}
    </PageAnimation>
  </ImageLazyloadProvider>
);

/**
 * This sets providers high enough in the component tree where they won't be unmounted
 * if/when the Layout component is. This prevents the logic in the providers from re-running on route changes.
 * https://www.gatsbyjs.com/docs/layout-components/#how-to-prevent-layout-components-from-unmounting
 */
export const wrapRootElement = ({ element }) => (
  <RootElementMDXProvider>
    <ThemeProvider>
      <AnalyticsProvider>
        {element}
      </AnalyticsProvider>
    </ThemeProvider>
  </RootElementMDXProvider>
);
